// BILLING-PLANS (docs/design/2026-10-27-wallet-pricing.md §3.5–§3.7): row contracts and jsonb
// shapes of recurring plans, invoices, quotes and build projects. Same conventions as `rows.ts`:
// keys are column names, nullability is the column's, enums are the CHECK's; bound to their
// tables in `contract-bindings.ts`. Money is integer minor units (cents) of the row's currency,
// never a float. Stripe ids are carried for the operator's cross-reference only.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import {
  BillingInterval,
  BuildMaintenanceShape,
  BuildProjectKind,
  BuildProjectStatus,
  ChargeKind,
  CollectionMethod,
  Currency,
  DiscountAppliesTo,
  InvoicePaymentMethod,
  InvoiceSource,
  InvoiceStatus,
  MaintenanceSupervision,
  MaintenanceTier,
  MilestoneStatus,
  QuoteStatus,
  SubscriptionStatus,
  SystemUsageBilling,
} from "./enums";
import { IsoDate, MicrosAmount, MinorAmount, Timestamp, Uuid } from "./primitives";

const actor = ActorShortColumns.shape;
const stamps = { created_at: Timestamp, updated_at: Timestamp };

// jsonb shapes ----------------------------------------------------------------------------------

/** What an invoice line is, for the screens ("Plan", "Maintenance · Standard × 2", "Sales DE, added 12 Nov"). */
export const InvoiceLineKind = z
  .enum([
    "plan",
    "extra_seats",
    "maintenance",
    "retainer",
    "proration",
    "discount",
    "setup",
    "milestone",
    "maintenance_yearly",
    "topup",
    "custom",
  ])
  .meta({ id: "InvoiceLineKind" });

/** One invoice (or upcoming-invoice) line as mirrored from Stripe; `amount_minor` is signed. */
export const InvoiceLine = z
  .strictObject({
    id: z.string().max(255),
    kind: InvoiceLineKind,
    description: z.string().max(500),
    quantity: z.int().min(0),
    unit_amount_minor: MinorAmount.nullable(),
    amount_minor: MinorAmount,
    /** The part of the invoice's discounts Stripe allocated to this line (absent: none). */
    discount_minor: MinorAmount.optional(),
    proration: z.boolean(),
    tier: MaintenanceTier.nullable(),
    period_start: Timestamp.nullable(),
    period_end: Timestamp.nullable(),
    /** The DE a proration is for, when CompanyOS can name it (the day it was added or retired). */
    membership_id: Uuid.nullable(),
    de_name: z.string().max(200).nullable(),
    /** A proration the change that made it attributes to several DEs (shared among them). */
    membership_ids: z.array(Uuid).max(200).optional(),
  })
  .meta({ id: "InvoiceLine" });
export type InvoiceLine = z.infer<typeof InvoiceLine>;

/** The next invoice as Stripe previews it (`invoice.upcoming`, or a refresh after a change). */
export const UpcomingInvoice = z
  .strictObject({
    next_payment_at: Timestamp.nullable(),
    subtotal_minor: MinorAmount,
    discount_minor: MinorAmount,
    total_minor: MinorAmount,
    currency: Currency,
    lines: z.array(InvoiceLine).max(200),
    fetched_at: Timestamp,
  })
  .meta({ id: "UpcomingInvoice" });
export type UpcomingInvoice = z.infer<typeof UpcomingInvoice>;

/** Per-company maintenance prices by tier (overrides the operator's list); cents per DE. */
export const MaintenanceOverrides = z
  .strictObject({
    standard: MinorAmount.min(0).optional(),
    advanced: MinorAmount.min(0).optional(),
    managed: MinorAmount.min(0).optional(),
  })
  .meta({ id: "MaintenanceOverrides" });
export type MaintenanceOverrides = z.infer<typeof MaintenanceOverrides>;

/**
 * What the subscription still bills above the company's terms until its next renewal: a change
 * that lowers the bill (a cheaper plan or price, a DE or person gone) is never credited
 * mid-period; Stripe keeps the paid item until the renewal, which applies it unprorated.
 * `revert` is the plan terms Stripe still bills, when a plan change caused the hold (sending them
 * back cancels the pending change at no charge).
 */
export const ScheduledChanges = z
  .strictObject({
    effective_at: Timestamp.nullable(),
    items: z
      .array(
        z.strictObject({
          item: z.enum(["plan", "extra_seats", "maintenance", "retainer"]),
          tier: MaintenanceTier.nullable(),
          from_quantity: z.int().min(0),
          to_quantity: z.int().min(0),
          from_unit_minor: MinorAmount.nullable(),
          to_unit_minor: MinorAmount.nullable(),
        }),
      )
      .max(50),
    revert: z
      .strictObject({
        plan_key: z.string(),
        grace_days: z.int().nullable(),
        base_price_override_minor: MinorAmount.nullable(),
        extra_seat_price_override_minor: MinorAmount.nullable(),
        included_seats_override: z.int().nullable(),
        maintenance_overrides: MaintenanceOverrides,
      })
      .nullable(),
    /** The plan version the held plan terms were paid at: sending `revert` restores it (r20 #1). */
    paid_plan_id: z.string().nullable().optional(),
  })
  .meta({ id: "ScheduledChanges" });
export type ScheduledChanges = z.infer<typeof ScheduledChanges>;

/** The Stripe subscription item of each maintenance tier on a company's subscription. */
export const MaintenanceItems = z
  .strictObject({
    standard: z.string().max(255).optional(),
    advanced: z.string().max(255).optional(),
    managed: z.string().max(255).optional(),
  })
  .meta({ id: "MaintenanceItems" });
export type MaintenanceItems = z.infer<typeof MaintenanceItems>;

/** A quote or one-off invoice line as drafted by the operator. */
export const DraftLine = z
  .strictObject({
    description: z.string().min(1).max(500),
    quantity: z.int().min(1).max(10_000),
    unit_amount_minor: MinorAmount.min(0).max(10_000_000_000),
    catalog_key: z.string().max(64).nullable(),
    milestone_id: Uuid.nullable(),
  })
  .meta({ id: "DraftLine" });
export type DraftLine = z.infer<typeof DraftLine>;

// Control-plane rows -----------------------------------------------------------------------------

/** A platform plan version (`cp.plan`). */
export const PlatformPlan = z
  .object({
    id: Uuid,
    key: z.string(),
    name: z.string(),
    base_price_minor: MinorAmount,
    currency: Currency,
    interval: BillingInterval,
    active: z.boolean(),
    included_seats: z.int(),
    extra_seat_price_minor: MinorAmount,
    stripe_price_id: z.string().nullable(),
    extra_seat_stripe_price_id: z.string().nullable(),
    version: z.int(),
    effective_from: IsoDate,
    grace_days: z.int(),
    system_usage: SystemUsageBilling,
    /** ONBOARDING-P3 (PLAN-DES): the digital employees the plan includes (null: no limit). */
    included_des: z.int().nullable(),
    created_by_user_id: Uuid.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "PlatformPlan" });
export type PlatformPlan = z.infer<typeof PlatformPlan>;

/** A maintenance tier version (`cp.maintenance_plan`). */
export const MaintenancePlan = z
  .object({
    id: Uuid,
    key: z.string(),
    name: z.string(),
    tier: MaintenanceTier,
    price_minor: MinorAmount,
    currency: Currency,
    interval: BillingInterval,
    ag_cap: z.int(),
    supervision: MaintenanceSupervision,
    stripe_price_id: z.string().nullable(),
    version: z.int(),
    effective_from: IsoDate,
    active: z.boolean(),
    created_by_user_id: Uuid.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "MaintenancePlan" });
export type MaintenancePlan = z.infer<typeof MaintenancePlan>;

/** A one-off line in the operator's catalog (`cp.charge_catalog`). */
export const ChargeCatalogItem = z
  .object({
    id: Uuid,
    key: z.string(),
    name: z.string(),
    kind: ChargeKind,
    default_price_minor: MinorAmount,
    currency: Currency,
    taxable: z.boolean(),
    active: z.boolean(),
    created_by_user_id: Uuid.nullable(),
    ...stamps,
  })
  .meta({ id: "ChargeCatalogItem" });
export type ChargeCatalogItem = z.infer<typeof ChargeCatalogItem>;

/** The platform defaults a company inherits (`cp.billing_default`). */
export const BillingDefaults = z
  .object({
    key: z.literal("default"),
    net_terms_days: z.int(),
    setup_fee_minor: MinorAmount,
    reminder_day: z.int(),
    payment_methods: z.array(InvoicePaymentMethod),
    updated_by_user_id: Uuid.nullable(),
    updated_at: Timestamp,
  })
  .meta({ id: "BillingDefaults" });
export type BillingDefaults = z.infer<typeof BillingDefaults>;

// Cell rows --------------------------------------------------------------------------------------

/** How a company pays (`cell.billing_account`); null terms / methods = the platform default. */
export const BillingAccount = z
  .object({
    company_id: Uuid,
    billing_email: z.string().nullable(),
    collection_method: CollectionMethod,
    net_terms_days: z.int().nullable(),
    payment_methods: z.array(InvoicePaymentMethod).nullable(),
    pm_brand: z.string().nullable(),
    pm_last4: z.string().nullable(),
    setup_fee_minor: MinorAmount.nullable(),
    invoice_topups_allowed: z.boolean(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "BillingAccount" });
export type BillingAccount = z.infer<typeof BillingAccount>;

/** A company's one subscription (`cell.company_subscription`): the plan and every DE's maintenance. */
export const CompanySubscription = z
  .object({
    company_id: Uuid,
    id: Uuid,
    plan_id: Uuid,
    plan_key: z.string(),
    plan_version: z.int(),
    status: SubscriptionStatus,
    base_price_override_minor: MinorAmount.nullable(),
    extra_seat_price_override_minor: MinorAmount.nullable(),
    included_seats_override: z.int().nullable(),
    maintenance_overrides: MaintenanceOverrides,
    people_qty: z.int(),
    extra_seat_qty: z.int(),
    current_period_start: Timestamp.nullable(),
    current_period_end: Timestamp.nullable(),
    trial_end: Timestamp.nullable(),
    grace_days: z.int().nullable(),
    past_due_since: Timestamp.nullable(),
    grace_until: Timestamp.nullable(),
    reminded_at: Timestamp.nullable(),
    suspended_at: Timestamp.nullable(),
    delinquent_invoice_id: Uuid.nullable(),
    upcoming: UpcomingInvoice.nullable(),
    cancel_at_period_end: z.boolean(),
    cancelled_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "CompanySubscription" });
export type CompanySubscription = z.infer<typeof CompanySubscription>;

/** An invoice of any origin, mirrored from Stripe (`cell.invoice`). */
export const Invoice = z
  .object({
    company_id: Uuid,
    id: Uuid,
    number: z.string().nullable(),
    source: InvoiceSource,
    status: InvoiceStatus,
    collection_method: CollectionMethod,
    payment_method: z.string().nullable(),
    currency: Currency,
    subtotal_minor: MinorAmount,
    discount_minor: MinorAmount,
    tax_minor: MinorAmount,
    total_minor: MinorAmount,
    amount_paid_minor: MinorAmount,
    amount_due_minor: MinorAmount,
    due_date: IsoDate.nullable(),
    period_start: Timestamp.nullable(),
    period_end: Timestamp.nullable(),
    hosted_url: z.string().nullable(),
    pdf_url: z.string().nullable(),
    lines: z.array(InvoiceLine),
    memo: z.string().nullable(),
    quote_id: Uuid.nullable(),
    project_id: Uuid.nullable(),
    milestone_id: Uuid.nullable(),
    topup_amount_micros: MicrosAmount.nullable(),
    topup_credited_at: Timestamp.nullable(),
    finalized_at: Timestamp.nullable(),
    sent_at: Timestamp.nullable(),
    paid_at: Timestamp.nullable(),
    voided_at: Timestamp.nullable(),
    reminded_at: Timestamp.nullable(),
    payment_failed_at: Timestamp.nullable(),
    overdue_at: Timestamp.nullable(),
    paid_reference: z.string().nullable(),
    marked_paid_by_user_id: Uuid.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Invoice" });
export type Invoice = z.infer<typeof Invoice>;

/** A percentage off the recurring invoices for N months (`cell.company_discount`). */
export const CompanyDiscount = z
  .object({
    company_id: Uuid,
    id: Uuid,
    label: z.string(),
    percent_bp: z.int(),
    months: z.int(),
    applies_to: DiscountAppliesTo,
    starts_on: IsoDate,
    ends_on: IsoDate,
    ended_at: Timestamp.nullable(),
    applied_at: Timestamp.nullable(),
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "CompanyDiscount" });
export type CompanyDiscount = z.infer<typeof CompanyDiscount>;

/** A one-off charge waiting for (or on) an invoice: a setup fee on DE creation (`cell.billing_charge`). */
export const BillingCharge = z
  .object({
    company_id: Uuid,
    id: Uuid,
    kind: InvoiceSource,
    description: z.string(),
    amount_minor: MinorAmount,
    currency: Currency,
    membership_id: Uuid.nullable(),
    invoice_id: Uuid.nullable(),
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "BillingCharge" });
export type BillingCharge = z.infer<typeof BillingCharge>;

/** A quote, mirrored from Stripe (`cell.quote`). */
export const Quote = z
  .object({
    company_id: Uuid,
    id: Uuid,
    number: z.string().nullable(),
    title: z.string(),
    status: QuoteStatus,
    lines: z.array(DraftLine),
    total_minor: MinorAmount,
    currency: Currency,
    expires_at: Timestamp,
    memo: z.string().nullable(),
    pdf_url: z.string().nullable(),
    hosted_url: z.string().nullable(),
    invoice_id: Uuid.nullable(),
    project_id: Uuid.nullable(),
    sent_at: Timestamp.nullable(),
    accepted_at: Timestamp.nullable(),
    accepted_by_membership_id: Uuid.nullable(),
    canceled_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Quote" });
export type Quote = z.infer<typeof Quote>;

/** A custom or complex build billed by milestones (`cell.build_project`). */
export const BuildProject = z
  .object({
    company_id: Uuid,
    id: Uuid,
    name: z.string(),
    kind: BuildProjectKind,
    status: BuildProjectStatus,
    quote_id: Uuid.nullable(),
    total_minor: MinorAmount,
    currency: Currency,
    maintenance_shape: BuildMaintenanceShape,
    maintenance_pct_bp: z.int().nullable(),
    retainer_minor: MinorAmount.nullable(),
    delivered_on: IsoDate.nullable(),
    next_maintenance_on: IsoDate.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "BuildProject" });
export type BuildProject = z.infer<typeof BuildProject>;

/** One milestone of a build (`cell.build_milestone`). */
export const BuildMilestone = z
  .object({
    company_id: Uuid,
    id: Uuid,
    project_id: Uuid,
    seq: z.int(),
    name: z.string(),
    amount_minor: MinorAmount,
    due_on: IsoDate.nullable(),
    status: MilestoneStatus,
    invoice_id: Uuid.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "BuildMilestone" });
export type BuildMilestone = z.infer<typeof BuildMilestone>;
